import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useCart } from "../../Cart/cartContext";
import { useNavigate } from "react-router-dom";
import { FaStar, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import ImageWithPlaceholder from "../../common/ImageWithPlaceholder";
import { getDesigners, DesignerNames } from "../../../pages/ProductDetail";
import styles from "./ProductModal.module.css";

const SIZES = [
  { label: "XS", details: 'Extra Small — Chest 32"–34", Waist 28–30' },
  { label: "S", details: 'Small — Chest 34"–36", Waist 30–32' },
  { label: "M", details: 'Medium — Chest 38"–40", Waist 32–34' },
  { label: "L", details: 'Large — Chest 42"–44", Waist 34–36' },
  { label: "XL", details: 'Extra Large — Chest 46"–48", Waist 36–38' },
  { label: "XXL", details: '2X Large — Chest 50"–52", Waist 38–40' },
  { label: "XXXL", details: '3X Large — Chest 54"–56", Waist 40–42' },
];

const ProductModal = ({ showModal, setShowModal, product }) => {
  const { cart, setCart } = useCart();
  const navigate = useNavigate();
  const [hasAdded, setHasAdded] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeColor, setActiveColor] = useState(
    product.colors?.length ? product.colors[0] : null
  );
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Active colour's photos take over; fall back to the product's own
  const colorImages = activeColor
    ? [activeColor.image, ...(activeColor.images || [])].filter(Boolean)
    : [];
  const productImages = colorImages.length
    ? colorImages
    : product.image
    ? [product.image, ...(product.images || [])]
    : product.images || [];
  const totalImages = productImages.length;

  const chooseColor = (color) => {
    setActiveColor(color);
    setCurrentImageIndex(0);
  };

  const addToCart = () => {
    const colorName = activeColor?.name || "";
    // Same product in a different colour is its own cart line
    const matches = (item) =>
      item.product.id === product.id && (item.color || "") === colorName;
    const productExists = cart.some(matches);
    if (productExists) {
      setCart((currentCart) =>
        currentCart.map((item) =>
          matches(item) ? { ...item, quantity: item.quantity + quantity } : item
        )
      );
    } else {
      setCart((currentCart) => [
        ...currentCart,
        { product, quantity, color: colorName },
      ]);
    }
    setHasAdded(true);
  };

  const handleGoToCart = () => navigate("/cart");
  const handleCloseModal = () => {
    setShowModal(false);
    setHasAdded(false);
  };

  const handlePrevImage = () =>
    setCurrentImageIndex((i) => (i === 0 ? totalImages - 1 : i - 1));
  const handleNextImage = () =>
    setCurrentImageIndex((i) => (i === totalImages - 1 ? 0 : i + 1));

  const formattedPrice = new Intl.NumberFormat("en-NG", {
    style: "decimal",
    maximumFractionDigits: 2,
  }).format(product.price);

  return (
    <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
      <Modal.Header closeButton />
      <Modal.Body className={styles.body} style={{ padding: "0 1.25rem 1.25rem" }}>
        <div className={styles.grid}>
          {/* Gallery */}
          <div className={styles.gallery}>
            <ImageWithPlaceholder
              src={productImages[currentImageIndex]}
              alt={product.name}
              className={styles.galleryImg}
              wrapperStyle={{ position: "absolute", inset: 0, display: "block" }}
            />
            {totalImages > 1 && (
              <>
                <button
                  type="button"
                  className={`${styles.navBtn} ${styles.navPrev}`}
                  onClick={handlePrevImage}
                  aria-label="Previous image"
                >
                  <FaChevronLeft />
                </button>
                <button
                  type="button"
                  className={`${styles.navBtn} ${styles.navNext}`}
                  onClick={handleNextImage}
                  aria-label="Next image"
                >
                  <FaChevronRight />
                </button>
                <div className={styles.dots}>
                  {productImages.map((_, i) => (
                    <span
                      key={i}
                      className={`${styles.dot} ${
                        i === currentImageIndex ? styles.dotActive : ""
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Info */}
          <div className={styles.info}>
            <p className={styles.eyebrow}>Lormami</p>
            <h3 className={styles.name}>{product.name}</h3>
            <p className={styles.price}>₦ {formattedPrice}</p>

            {(product.colors || []).length > 0 && (
              <>
                <p className={styles.label} style={{ marginTop: "0.4rem" }}>
                  Colour{activeColor ? ` — ${activeColor.name}` : ""}
                </p>
                <div className={styles.swatches}>
                  {product.colors.map((color) => (
                    <button
                      type="button"
                      key={color.name}
                      className={
                        activeColor?.name === color.name
                          ? styles.swatchActive
                          : styles.swatch
                      }
                      style={{ backgroundColor: color.hex }}
                      title={color.name}
                      aria-label={`Colour ${color.name}`}
                      onClick={() => chooseColor(color)}
                    />
                  ))}
                </div>
              </>
            )}
            <div className={styles.rating}>
              {[...Array(5)].map((_, index) => (
                <FaStar
                  key={index}
                  size={14}
                  color={index < product.rating ? "#C9A24B" : "#E4E1DA"}
                />
              ))}
            </div>
            {product.description && (
              <p className={styles.desc}>{product.description}</p>
            )}

            {getDesigners(product).length > 0 && (
              <p className={styles.credit}>
                Designed by <DesignerNames product={product} />
              </p>
            )}

            <button
              type="button"
              className={styles.detailsLink}
              onClick={() => {
                handleCloseModal();
                navigate(`/product/${product._id || product.id}`);
              }}
            >
              View full details →
            </button>

            <p className={styles.label}>Select Size</p>
            <div className={styles.sizes}>
              {SIZES.map((size) => (
                <button
                  type="button"
                  key={size.label}
                  className={`${styles.sizePill} ${
                    selectedSize?.label === size.label ? styles.sizePillActive : ""
                  }`}
                  onClick={() => setSelectedSize(size)}
                >
                  {size.label}
                </button>
              ))}
            </div>
            {selectedSize && (
              <p className={styles.sizeDetail}>{selectedSize.details}</p>
            )}

            <p className={styles.label}>Quantity</p>
            <div className={styles.qtyRow}>
              <div className={styles.stepper}>
                <button
                  type="button"
                  className={styles.stepBtn}
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <span className={styles.qtyValue}>{quantity}</span>
                <button
                  type="button"
                  className={styles.stepBtn}
                  onClick={() => setQuantity((q) => q + 1)}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer style={{ border: "none", padding: "0 1.25rem 1.25rem" }}>
        <div className={styles.actions}>
          {!hasAdded ? (
            <>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={handleCloseModal}
              >
                Close
              </button>
              <button type="button" className={styles.btnPrimary} onClick={addToCart}>
                Add to Cart
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={handleCloseModal}
              >
                Continue Shopping
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleGoToCart}
              >
                Go to Cart
              </button>
            </>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ProductModal;
